import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  FormGroup,
} from '@mui/material';
import { fetchPatientTreatments } from '../../../api/patients';

const IEPServiceTherapiesField = ({
  studentId,
  typeOfUser,
  value,
  onChange,
  disabled = false,
}) => {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Extract all therapies with IEP configuration from treatments
  const iepTherapies = useMemo(() => {
    if (!treatments || treatments.length === 0) return [];

    const therapies = [];
    treatments.forEach((treatment) => {
      if (treatment.therapies && Array.isArray(treatment.therapies)) {
        treatment.therapies.forEach((therapy) => {
          // Check if therapy has IEP configuration
          const hasIEP =
            therapy.insuranceTypes?.includes('iep') ||
            (therapy.iepStartDate && therapy.iepEndDate && therapy.iepServiceFrequency);

          if (hasIEP && therapy.id) {
            therapies.push({
              id: therapy.id,
              name: therapy.therapy_name || `Therapy ${therapy.id}`,
            });
          }
        });
      }
    });

    return therapies;
  }, [treatments]);

  // Fetch treatments when studentId changes
  useEffect(() => {
    if (!studentId || !typeOfUser) return;

    const loadTreatments = async () => {
      setLoading(true);
      try {
        const fetchedTreatments = await fetchPatientTreatments(studentId, typeOfUser);
        if (fetchedTreatments && Array.isArray(fetchedTreatments)) {
          setTreatments(fetchedTreatments);
        } else {
          setTreatments([]);
        }
      } catch (error) {
        console.error('Error fetching treatments:', error);
        setTreatments([]);
      } finally {
        setLoading(false);
      }
    };

    loadTreatments();
  }, [studentId, typeOfUser]);

  const handleTherapyChange = (therapyId) => (event) => {
    const isChecked = event.target.checked;
    const currentValue = value || [];
    
    if (isChecked) {
      // Add therapy ID if not already present
      if (!currentValue.includes(therapyId)) {
        onChange([...currentValue, therapyId]);
      }
    } else {
      // Remove therapy ID
      onChange(currentValue.filter((id) => id !== therapyId));
    }
  };

  const selectedTherapyIds = useMemo(() => {
    return value || [];
  }, [value]);

  if (loading) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        Loading therapies...
      </Typography>
    );
  }

  if (iepTherapies.length === 0) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        No IEP services available
      </Typography>
    );
  }

  return (
    <Box>
      <FormGroup row>
        {iepTherapies.map((therapy) => (
          <FormControlLabel
            key={therapy.id}
            control={
              <Checkbox
                checked={selectedTherapyIds.includes(therapy.id)}
                onChange={handleTherapyChange(therapy.id)}
                disabled={disabled}
                sx={{
                  color: '#9291f0',
                  '&.Mui-checked': {
                    color: '#9291f0',
                  },
                }}
              />
            }
            label={therapy.name}
          />
        ))}
      </FormGroup>
    </Box>
  );
};

export default IEPServiceTherapiesField;

